<script setup>
import PluginsPage from "~/components/templates/PluginsPage.vue";
import Plugin from "~/components/organisms/Plugin.vue";
import BasePage from "~/components/templates/BasePage.vue";
import Breadcrumbs from "~/components/organisms/Breadcrumbs.vue";

const props = defineProps({
  data: Object
})

const {data} = props

const grouped_plugins = data.grouped_plugins
const name = data.name
const subheading = data.subheading
const showHeading = !data.hide_heading
const showSubheading = !!data.subheading && !data.hide_subheading
const showBreadcrumbs = !data.hide_breadcrumbs && !!data.breadcrumbs?.length

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: data.breadcrumbs_structured_data
    },
  ]
})

</script>

<template>
  <div v-if="showBreadcrumbs" class="container">
    <Breadcrumbs :breadcrumbs="data.breadcrumbs"/>
  </div>
  <PluginsPage>
    <template v-slot:[position] v-for="[position, plugins] of Object.entries(grouped_plugins)">
      <template v-for="plugin in plugins">
        <Plugin v-if="plugin.data !== false" :data="plugin.data" :name="plugin.name" :settings="plugin.settings"/>
      </template>
    </template>

    <template v-slot:ordinary-content>
      <BasePage v-if="showHeading || showSubheading">
        <template v-if="showHeading" v-slot:title>
          {{ name }}
        </template>

        <template v-if="showSubheading" v-slot:subheading>
          {{ subheading }}
        </template>
      </BasePage>
    </template>
  </PluginsPage>
</template>

<style scoped lang="scss">
.sizes {
  flex-wrap: wrap;
}

.left-part {
  margin-right: 74px;

  @include smd {
    margin-right: 0;
    width: 100%;
  }
}

.gallery-block {
  @include smd {
    max-width: 100%;
    margin: 0;
  }
}

.top-container {
  @include smd {
    flex-direction: column-reverse;
  }
}

.previews {
  display: grid;
  grid-template-columns: 24% 24% 24% 24%;
  gap: 18px;
  width: 96%;
  margin: 56px auto auto;

  @include smd {
    gap: 10px;
    width: 100%;
    margin: 0;
    grid-template-columns: repeat(2, 2fr);
    row-gap: 30px;
  }

  .preview {
    margin: 20px;

    @include smd {
      margin: 0;
    }

    .name {
      margin-top: 10px;
      font-size: 16px;
      font-weight: 600;
      font-style: normal;
      letter-spacing: normal;
      line-height: normal;
      text-align: left;
      height: 62px;
      color: #6e6e6e;

      @include smd {
        font-size: 12px;
        height: unset;
        margin-bottom: 10px;
      }
    }

    .product-attributes {
      margin-top: 30px;

      @include smd {
        margin-top: 10px;
        font-size: 11px;
      }

      .attribute-item {
        gap: 4px;

        .attribute-name {
          font-weight: 600;
        }
      }
    }
  }
}

.products {
  padding-top: 45px;
  border-top: 1px solid $border-grey;
}

.images {
  display: grid;
  grid-template-columns: repeat(3, 3fr);
  gap: 20px;

  @include smd {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: auto auto;
  }

  .image {
    @include smd {
      &:first-child {
        grid-column: 1 / 2;
        grid-row: 1 / 3;
        width: 100%;
        height: auto;
      }
    }

    &:nth-child(2),
    &:nth-child(3) {
      @include smd {
        grid-column: 2 / 3;
        height: auto;
        width: 92%;
      }
    }
  }
}

.category {
  background: #efefef;
  margin-right: 10px;
  padding: 5px 10px;
  margin-bottom: 10px;
}

.color {
  margin-right: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.color .square {
  min-width: 31px;
  min-height: 30px;
  border-radius: 3px;
}

.color span {
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  font-style: normal;
  letter-spacing: normal;
  line-height: normal;
  color: #6e6e6e;
  margin-top: 5px;
}

.panel {
  display: grid;
  margin-top: 20px;
  max-width: 425px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  grid-template-columns: 12.5% 12.5% 12.5% 12.5% 12.5% 12.5% 12.5% 12.5%;
}

.focus {
  background: $light-black;
  color: $white;
  padding: 7px;
}

.attributes {
  margin-top: 40px;
  display: grid;
  grid-template-columns: 50% 50%;
  row-gap: 20px;

  @include smd {
    row-gap: 10px;
    column-gap: 0;
  }
}

.attributes .attribute .icon {
  width: 46px;
  height: 49px;
  max-width: 46px;
  max-height: 49px;
  border-radius: 14px;
  background-color: #f5f5f5;
  display: flex;
  align-content: center;
  justify-content: center;

  @include smd {
    width: 42px;
    height: 42px;
  }
}

.attributes .attribute .icon img {
  height: 32px;
  display: flex;
  align-self: center;
  justify-self: center;
}

.attribute .attr-info span {
  margin-left: 25px;
  font-size: 18px;
  font-weight: 400;
  font-style: normal;
  letter-spacing: normal;
  line-height: 26px;
  text-align: left;

  @include smd {
    font-size: 13px;
    font-weight: 600;
  }

  &:last-child {
    font-size: 15px;

    @include smd {
      font-size: 9px;
      font-weight: 400;
      line-height: 15px;
    }
  }

  .attributes .attribute .attr-info span:last-child {
    font-size: 15px;
  }
}
</style>
